import React, { useState } from 'react';
import FilterIcon from '../../SVG/Filter';
import { useNavigate } from 'react-router-dom';

const ClaimStatusSection = () => {
    const [polNo, setPolNo] = useState('');
    const [eligibilityStatus, setEligibilityStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const apiURL = import.meta.env.VITE_API;

    const CLAIM_FILING_INFO =
        "If you are filing a claim on a Medicare Supplement policy, Part A and Part B claims are received electronically from Medicare. " +
        "It is not necessary to file a paper claim. The policy is set up for electronic crossover. " +
        "We follow all Medicare guidelines. No precertification is required. " +
        "Timely filing is 12 months from the date of service. " +
        "All benefits are based on medical necessity, eligibility at the time of service " +
        "and all policy provisions, limitations and exclusions. " +
        "Benefits given are not a guarantee of payments.";

    const fields = [
        { label: 'Policy Number', accessor: 'polNo' },
        { label: 'Type Of Coverage', accessor: 'coverageType' },
        { label: 'Effective Date', accessor: 'issueDate' },
        { label: 'Paid To Date', accessor: 'paidToDate' },
        { label: 'Status Description', accessor: 'statusDesc' },
        { label: 'Last Name Abbreviation', accessor: 'lastNameAbbr' },
        { label: 'Coverage Description', accessor: 'coverageDescription' },
        { label: 'Claim Filing Info', accessor: '__CLAIM_FILING_INFO__' },
        { label: 'Mailing Address', accessor: 'mailingAddress' },
        { label: 'Fax Number', accessor: 'faxNumber' },
        { label: 'Payer ID', accessor: 'payerID' },
    ];

    const handleSearch = async () => {
        setSearched(true);

        if (!polNo) {
            setError('Please enter a policy number.');
            setEligibilityStatus(null);
            return;
        }

        if (polNo.length !== 10) {
            setError('Policy number must be 10 digits.');
            setEligibilityStatus(null);
            return;
        }

        setLoading(true);
        setError(null);
        setEligibilityStatus(null);

        try {
            const res = await fetch(
                `${apiURL}/Provider/getEligibilityStatus?polNo=${encodeURIComponent(polNo)}`,
                { method: 'POST' }
            );

            // if (res.status === 401) { navigate('/login'); throw new Error('unauthorized'); }
            // if (res.status === 403) { navigate('/home'); throw new Error('forbidden'); }

            if (!res.ok) {
                throw new Error(`HTTP ${res.status} - ${res.statusText}`);
            }

            const data = await res.json();

            // Normalize to a single object (in case API returns array)
            const result = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);

            // Treat empty object as "no data"
            const hasData =
                result &&
                typeof result === 'object' &&
                Object.keys(result).some(k => result[k] !== null && result[k] !== '');

            if (!hasData) {
                setEligibilityStatus(null);
                setError('No eligibility information found for that policy number.');
                return;
            }

            setEligibilityStatus(result);
        } catch (err) {
            console.error('Error fetching eligibility status:', err);
            setError('Failed to load eligibility status. Please try again.');
            setEligibilityStatus(null);
        } finally {
            setLoading(false);
        }
    };

    // Only show the hardcoded Claim Filing Info when it looks like Medicare coverage
    const shouldShowClaimFilingInfo =
        (eligibilityStatus?.coverageType || '').toLowerCase().includes('medicare');

    return (
        <div className="container container-section">
            <div className="filter-box">
                <FilterIcon />
                <div className="filter-item">
                    <label htmlFor="polNo" className="filterLabel">
                        Policy Number:
                    </label>
                    <input
                        type="text"
                        id="polNo"
                        className="filterInput"
                        value={polNo}
                        onChange={e => setPolNo(e.target.value)}
                        maxLength={10}
                        required
                    />
                </div>

                <div className="filter-button-box">
                    <div className="filter-item">
                        <button
                            type="button"
                            className="filterButton"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && <div className="claim-status__error">{error}</div>}

            {/* Loading */}
            {loading && (
                <div className="claim-status__card">
                    <h4>Loading eligibility status...</h4>
                </div>
            )}

            {/* Result */}
            {!loading && eligibilityStatus && (
                <div className="claim-status__card">
                    <h3 className="claim-status__title">Eligibility Status</h3>

                    <div className="claim-status__details">
                        {fields.map(field => {
                            // Hide Claim Filing Info unless Medicare coverage
                            if (field.accessor === '__CLAIM_FILING_INFO__' && !shouldShowClaimFilingInfo) {
                                return null;
                            }

                            const value =
                                field.accessor === '__CLAIM_FILING_INFO__'
                                    ? CLAIM_FILING_INFO
                                    : eligibilityStatus?.[field.accessor] ?? '-';

                            return (
                                <div key={field.label} className="claim-status__row">
                                    <div className="claim-status__row-label">{field.label}</div>
                                    <div className="claim-status__row-value">{value}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* No Results */}
            {!loading && !eligibilityStatus && !error && searched && (
                <div className="claim-status__card">
                    <h4>No eligibility information found.</h4>
                </div>
            )}
        </div>
    );
};

export default ClaimStatusSection;
